﻿import type { IReportFields, LOCALE_CODE } from './contentful'
import type { EMainCategories } from '@/domains/investor/const/MainCategories.enum'
import type { ESecondCategories } from '@/domains/investor/const/SecondCategories.enum'
import type { Document } from '@contentful/rich-text-types'

export type IAdaptChartType = 'line' | 'doughnut' | 'bar' | 'barY'

export interface TImage {
  url: string;
  alternative: string;
  width?: string;
  height?: string;
}

export interface TList {
  icon: TImage;
  type: string;
  items: { title: string }[];
  layout?: string;
}

export enum KeyboardKey {
  Escape = 'Escape',
  ArrowDown = 'ArrowDown',
  ArrowUp = 'ArrowUp'
}

export type LocalizedValue<T> = {
  [locale in LOCALE_CODE]: T | T[];
};

export interface IAdaptAuthor {
  name: string;
  title: string;
  description: string;
  image: string;
}

export interface IAdaptArticle {
  title: string;
  description: string;
  date: string;
  image: string;
  thumbnailWeb: string;
  url: string;
  author: IAdaptAuthor;
  content: string;
  urlsByIsoCode: Record<string, string>;
}

export interface IAdaptReport {
  title: string,
  date: string,
  url: string,
  urlsByIsoCode: Record<LOCALE_CODE, string>,
  categories: IReportFields['categories'][]
  mainCategory: string,
  group: string,
  content: Document,
  mappedSecondCategories: number[],
  mappedMainCategory: number,
  files: IAdaptRDKFile[],
}

export interface IAdaptContent {
  content: string;
}

export interface IRDKFile {
  label: string,
  bytes: number,
  format: string,
  url: string
}

export interface IAdaptRDKFile {
  label: string,
  size: string,
  format: string,
  url: string
}

export interface IFileRDK {
  size: number,
  format: string,
  original_url: string,
  bytes: number
}

export enum ELocale {
  PL = 'pl-PL',
  EN = 'en-US',
}

export interface ReportsQuery {
  locale?: string;
  mainCategory?: string;
  secondCategory?: string | string[];
  search?: string;
  year?: string | string[];
  months?: string | string[];
  page?: string;
  perPage?: string;
}

/** Investor relations module — the mandatory group value stored in Contentful. */
export const REPORT_GROUP: IReportFields['group'] = 'RelacjeInwestorskie'

export const INVESTOR_FIRST_YEAR = 2010

export interface IReportListItem {
  title: string;
  date: string;
  url: string;
}

export interface IReportsResponse {
  reports: IReportListItem[];
  total: number;
}

export interface CalendarEventsQuery {
  locale?: string;
}

export interface ICalendarEventListItem {
  id: string;
  title: string;
  date: string;
  content: Document | null;
}

export interface ICalendarEventsResponse {
  events: ICalendarEventListItem[];
  total: number;
}

export interface BondsQuery {
  locale?: string;
}

export interface IBondsFilesSection {
  title: string;
  files: IAdaptRDKFile[];
}

export interface IBondsItem {
  id: string;
  title: string;
  table: Document | null;
  statements: IBondsFilesSection | null;
  information: IBondsFilesSection | null;
}

export interface IBondsResponse {
  bonds: IBondsItem | null;
}
export interface IAdaptBoardMember {
  boardMemberName: string,
  boardMemberRole: string,
  slug?: string,
  boardMemberLead?: string,
  boardMemberPhoto?: string,
  boardMemberDescription?: Document,
  duties?: string[],
  supervisor?: string[],
  linkedInLink?: string
}

export interface IAdaptTeamMembers {
  boardMembers: IAdaptBoardMember[]
  boardTitle: string,
  boardLead?: string,
  headingType?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'h7'
}

export type Podcast = 'youtube' | 'spotify' | 'apple'

export interface VideocastUrl {
  url: string;
  type: Podcast;
  icon: string;
  label: string;
}

export interface IAdaptVideocast {
  title: string;
  description: string;
  date: string;
  image: string;
  thumbnailWeb: string;
  url: Record<string, string>;
  youtubeId?: string;
  vimeoId?: string;
  urls: VideocastUrl[];
  author: IAdaptAuthor;
  content: string;
  urlsByIsoCode: Record<string, string>;
}

export interface IERecruiterOffer {
  jobOfferId: string;
  title: string;
  location: string;
  region: {
    id: string;
    name: string;
  };
  urlWithLayout: string;
  publishDate: string;
  additionalFields: Record<string, string[]>;
  departments: string[];
}

export interface IOffer {
  id: string;
  title: string;
  location: string;
  region: string;
  url: string;
  publishedDate: string;
  jobType?: string;
}

export type IQuery = Record<string, string | string[]>

export type I18nParamsLocale = 'pl' | 'en'
export type I18nParams = Partial<Record<I18nParamsLocale, { url: string }>>

export type HeadLink = {
  rel: string
  href: string
  hid?: string
  hreflang?: string
  type?: string
}

export type HeadMeta = {
  name?: string
  content?: string
}

export interface IAdaptShareholders {
  title: string,
  numberOfShares: string,
  chart: IAdaptChart | null,
  table: IAdaptTable | null,
  tableSummary: string[],
}
export interface IAdaptTable {
  id: string,
  title: string,
  table: string,
  info?: string,
}

export interface IAdaptChartData {
  label: string,
  values: number[],
  displayValues?: string[],
  color?: string,
}

export interface IAdaptChart {
  label: string,
  title: string,
  labels: string[],
  type: IAdaptChartType,
  chartData: IAdaptChartData[],
}
export interface IAdaptFaqItem {
  id: string,
  question: string,
  answer: string,
}
export interface IAdaptFaq {
  id: string,
  title: string,
  items: IAdaptFaqItem[],
}
